import type {
  TerraUIMessage} from '@terra/shared-dto-chat-types';
import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
} from '@terra/shared-dto-chat-types';
import type { CoPilotTraceExportPayload } from './co-pilot-trace-export';
import {
  COPILOT_SESSION_OBJECT_KIND,
  type CoPilotSessionObjectKind,
  type CoPilotSessionObjectSource,
  extractCoPilotSessionObjectRefs,
  filterCoPilotSessionObjectRefs,
} from './co-pilot-session-objects';
import { extractCoPilotSessionHttpExchanges } from './co-pilot-session-http-exchanges';
import { buildCoPilotTranscriptTurns } from './co-pilot-transcript-export';

export type CoPilotSessionCsvKind = 'transcript' | 'objects' | 'http_exchanges';

export type CoPilotSessionExportInspection = {
  toolCallCount: number;
  httpExchangeCount: number;
  transcriptTurnCount: number;
  objects: {
    total: number;
    byKind: Partial<Record<CoPilotSessionObjectKind, number>>;
    createdFindings: number;
    createdSignals: number;
    mentionedFindings: number;
    mentionedSignals: number;
  };
};

export function inspectCoPilotSessionExportData({
  messages,
  exportPayload,
}: {
  messages: TerraUIMessage[];
  exportPayload: CoPilotTraceExportPayload;
}): CoPilotSessionExportInspection {
  const objects = extractCoPilotSessionObjectRefs({ messages, exportPayload });
  const httpRows = extractCoPilotSessionHttpExchanges({
    messages,
    exportPayload,
  });

  let toolCallCount = 0;
  for (const message of messages) {
    for (const part of message.parts) {
      if (
        part.type === TerraUIMessageDataPartName &&
        part.data?.type === DataMessageTypeEnum.ToolCallComplete
      ) {
        toolCallCount += 1;
      }
    }
  }

  const byKind: Partial<Record<CoPilotSessionObjectKind, number>> = {};
  for (const object of objects) {
    byKind[object.objectKind] = (byKind[object.objectKind] ?? 0) + 1;
  }

  const countByKindAndSource = ({
    objectKind,
    source,
  }: {
    objectKind: CoPilotSessionObjectKind;
    source: CoPilotSessionObjectSource;
  }) =>
    filterCoPilotSessionObjectRefs({
      objects,
      objectKinds: [objectKind],
      sources: [source],
    }).length;

  return {
    toolCallCount,
    httpExchangeCount: httpRows.length,
    transcriptTurnCount: buildCoPilotTranscriptTurns(messages).length,
    objects: {
      total: objects.length,
      byKind,
      createdFindings: countByKindAndSource({
        objectKind: COPILOT_SESSION_OBJECT_KIND.FINDING,
        source: 'created',
      }),
      createdSignals: countByKindAndSource({
        objectKind: COPILOT_SESSION_OBJECT_KIND.SIGNAL,
        source: 'created',
      }),
      mentionedFindings: countByKindAndSource({
        objectKind: COPILOT_SESSION_OBJECT_KIND.FINDING,
        source: 'mentioned',
      }),
      mentionedSignals: countByKindAndSource({
        objectKind: COPILOT_SESSION_OBJECT_KIND.SIGNAL,
        source: 'mentioned',
      }),
    },
  };
}

export function summarizeCoPilotSessionExportInspection({
  inspection,
}: {
  inspection: CoPilotSessionExportInspection;
}): string {
  const parts = [
    `${inspection.toolCallCount} tool call(s)`,
    `${inspection.httpExchangeCount} HTTP exchange(s)`,
    `${inspection.objects.total} session object(s)`,
    `${inspection.objects.createdFindings} finding(s) created`,
    `${inspection.objects.createdSignals} signal(s) created`,
  ];
  return parts.join(', ');
}

export function countFilteredSessionObjectRows({
  messages,
  exportPayload,
  objectKinds,
  sources,
}: {
  messages: TerraUIMessage[];
  exportPayload: CoPilotTraceExportPayload;
  objectKinds?: CoPilotSessionObjectKind[];
  sources?: CoPilotSessionObjectSource[];
}): number {
  return filterCoPilotSessionObjectRefs({
    objects: extractCoPilotSessionObjectRefs({ messages, exportPayload }),
    objectKinds,
    sources,
  }).length;
}

export function formatCoPilotEmptySessionMarkdownExportError(): string {
  return 'Nothing in this chat matches that export type. Try a transcript export instead.';
}

export function formatCoPilotEmptySessionExportError({
  csvKind,
}: {
  inspection: CoPilotSessionExportInspection;
  csvKind: CoPilotSessionCsvKind;
  objectKinds?: CoPilotSessionObjectKind[];
  sources?: CoPilotSessionObjectSource[];
}): string {
  if (csvKind === 'http_exchanges') {
    return 'No HTTP traffic in this chat matches that export.';
  }
  if (csvKind === 'transcript') {
    return 'No messages in this chat to export yet.';
  }

  return 'Nothing in this chat matches that export type. Try a transcript export instead.';
}
